/**
 * Registry of pure pre-publish audit checks.
 */

import { iterateAllSections } from './iterate-sections.js';
import { walkTextNodes } from './text-walker.js';
import {
  collectUsedClientKeys,
  findBoundButEmptyFields,
  normalizeClientInfo,
  validateClientInfoForExport
} from './client-info.js';
import { isSourceDomainUrl, LOGO_SENTINEL_VALUE } from './source-domain-url.js';
import { scanAllLinks } from './section-link-scan.js';
import { parseCssColor } from './color-utils.js';
import { assessContrast } from './wcag-contrast.js';
import { fnv1a64Hex } from './collab-protocol.js';

/**
 * Accept-identity policy version (P3). Bump ONLY when the meaning of an accept
 * changes — every existing accept stops matching, which is the point: a policy
 * change must not silently keep waiving findings under the old rules.
 */
export const ACCEPT_POLICY_VERSION = '1';

/**
 * Build the durable identity of "the operator decided this finding is fine".
 *
 *   v1:<checkId>:<stableTarget>:<evidenceFingerprint>:<policyVersion>
 *
 * Two things it is deliberately NOT built from:
 *   - `finding.id`, which embeds array indices and match offsets, so an accept
 *     keyed on it would break the moment a section is reordered;
 *   - `groupKey`, which is PRESENTATION. Every page missing an OG image shares
 *     one groupKey, so accepting a page would have accepted every page —
 *     including pages created later.
 *
 * The evidence fingerprint is what makes an accept expire honestly: it hashes
 * the thing being judged, so editing that thing re-raises the finding instead
 * of silently keeping the old waiver.
 */
export function buildAcceptKey(checkId, target, evidence = '') {
  const t = String(target ?? '').trim();
  if (!checkId || !t) return null;
  const fingerprint = fnv1a64Hex(String(evidence ?? ''));
  return `v1:${checkId}:${t}:${fingerprint}:${ACCEPT_POLICY_VERSION}`;
}

export const SEVERITY = {
  BLOCKING: 'blocking',
  WARNING: 'warning',
  CLEANUP: 'cleanup'
};

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function safeId(value) {
  return (
    String(value == null ? '' : value)
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'item'
  );
}

export function makeFinding({
  id,
  checkId,
  severity,
  title,
  detail,
  location = null,
  fix = null,
  groupKey = null,
  groupLabel = null,
  clusterFix = null,
  accept = null,
  kind = CHECK_KIND[checkId] || 'content'
}) {
  const finding = { id: `${checkId}:${id}`, checkId, kind, severity, title, detail, location, fix };
  // Optional grouping fields — only attached when the check opted in. The
  // audit tab clusters consecutive same-groupKey findings within a severity
  // section; findings without groupKey render flat (backwards compat).
  if (groupKey) finding.groupKey = groupKey;
  if (groupLabel) finding.groupLabel = groupLabel;
  // v1.2 — clusterFix. When the first finding in a cluster carries this,
  // the cluster header renders a "Fix all" button. Dispatched via the
  // 'pre-publish-audit:cluster-fix' event, handled in pre-publish-audit-actions.js.
  if (clusterFix) finding.clusterFix = clusterFix;
  // P3 — acceptKey. OPT-IN per check, with no fallback ladder: a check that
  // does not pass `accept` produces a finding that cannot be accepted, and the
  // UI renders no Accept affordance for it. That is the correct outcome for
  // data-integrity findings (duplicate eids), for anything BLOCKING that must
  // actually be fixed, and for audit-engine crashes — an internal error is not
  // a decision the operator can make.
  if (accept && accept.target) {
    const key = buildAcceptKey(checkId, accept.target, accept.evidence);
    if (key) finding.acceptKey = key;
  }
  return finding;
}

/**
 * Parsed-section cache (P7).
 *
 * Twelve checks each parse the SAME section html, and the full tier runs on
 * every idle tick, so an unchanged project re-parsed its entire corpus for
 * nothing. Validity is derived from the INPUT — a content hash of the html —
 * and never from an event.
 *
 * That last part is the whole design. `state-changed` fires on every `update()`
 * INCLUDING inside a transaction, so a cache invalidated on it would never
 * survive a scrub and would be strictly slower than no cache at all. The repo
 * already learned this once (16C2); this is the same rule applied here.
 *
 * The counters are not decoration either: a cache whose saving is not measured
 * is a performance claim, not a property of the code.
 */
const _parseCache = new Map();
const PARSE_CACHE_MAX = 400;
export const parseStats = { hits: 0, misses: 0 };

/** Test/diagnostic seam. */
export function _resetParseCache() {
  _parseCache.clear();
  parseStats.hits = 0;
  parseStats.misses = 0;
}

function parseSection(ctx, parseHTML) {
  const html = ctx.html || '';
  const key = `${html.length}:${fnv1a64Hex(html)}`;
  const hit = _parseCache.get(key);
  if (hit !== undefined) {
    parseStats.hits += 1;
    return hit;
  }
  parseStats.misses += 1;
  const doc = parseHTML(`<div data-pb-audit-root>${html}</div>`);
  const root = doc.querySelector?.('[data-pb-audit-root]') || doc.body?.firstElementChild || null;
  // Bounded: oldest-first eviction keeps a long session from growing without
  // limit on a project whose sections churn.
  if (_parseCache.size >= PARSE_CACHE_MAX) {
    _parseCache.delete(_parseCache.keys().next().value);
  }
  _parseCache.set(key, root);
  return root;
}

function closestEid(el) {
  return el?.closest?.('[data-pb-eid]')?.getAttribute?.('data-pb-eid') || '';
}

function locationFor(ctx, el = null, extra = {}) {
  return {
    pageId: ctx.pageId || null,
    pageName: ctx.pageName || '',
    instanceId: ctx.instanceId || null,
    sectionPath: ctx.sectionPath || '',
    sharedKind: ctx.sharedKind || null,
    eid: extra.eid || closestEid(el) || null,
    ...extra
  };
}

function snippet(text, start, length, radius = 28) {
  const raw = String(text || '');
  const beforeStart = Math.max(0, start - radius);
  const afterEnd = Math.min(raw.length, start + length + radius);
  return {
    beforeCtx: raw.slice(beforeStart, start),
    match: raw.slice(start, start + length),
    afterCtx: raw.slice(start + length, afterEnd)
  };
}

function textLocation(ctx, node, matchStart, matchText) {
  const eid = closestEid(node.parentElement || node.parentNode);
  return locationFor(ctx, node.parentElement || null, {
    eid,
    ...snippet(node.nodeValue || '', matchStart, matchText.length)
  });
}

function allPageContexts(state) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  return pages.map((page, pageIndex) => {
    const sections = [];
    const nav = state?.sharedSections?.navigation;
    if (nav?.html) {
      sections.push({
        html: nav.html,
        sectionPath: 'sharedSections.navigation.html',
        instanceId: nav.instanceId || null,
        kind: 'shared',
        sharedKind: 'navigation',
        pageId: page.id,
        pageName: page.name || page.id || ''
      });
    }
    for (const [sectionIndex, section] of (page.sections || []).entries()) {
      if (!section?.html) continue;
      sections.push({
        html: section.html,
        sectionPath: `pages.${pageIndex}.sections.${sectionIndex}.html`,
        instanceId: section.instanceId || null,
        kind: 'page-section',
        pageId: page.id,
        pageName: page.name || page.id || '',
        pageIndex,
        sectionIndex
      });
    }
    const footer = state?.sharedSections?.footer;
    if (footer?.html) {
      sections.push({
        html: footer.html,
        sectionPath: 'sharedSections.footer.html',
        instanceId: footer.instanceId || null,
        kind: 'shared',
        sharedKind: 'footer',
        pageId: page.id,
        pageName: page.name || page.id || ''
      });
    }
    return { page, pageIndex, sections };
  });
}

function pageSlugPaths(state, generateSlug) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const existing = new Set();
  const paths = new Set(['', '/', 'index', '/index', 'index.html', '/index.html']);
  pages.forEach((page, index) => {
    if (index === 0) return;
    const slug = generateSlug(page?.name || `page-${index + 1}`, existing);
    for (const value of [slug, `${slug}.html`]) {
      paths.add(value);
      paths.add(`/${value}`);
    }
  });
  return paths;
}

function normalizeInternalHref(href) {
  const raw = clean(href);
  if (!raw) return '';
  if (/^(https?:|mailto:|tel:|sms:|pb-client:|javascript:)/i.test(raw)) return null;
  if (raw.startsWith('#')) return null;
  return (
    raw
      .split('#')[0]
      .split('?')[0]
      .replace(/^\.?\//, '/')
      .replace(/\/+$/g, '') || '/'
  );
}

function collectIdsForPage(pageCtx, parseHTML) {
  const ids = new Set();
  for (const sectionCtx of pageCtx.sections) {
    const root = parseSection(sectionCtx, parseHTML);
    if (!root) continue;
    for (const el of root.querySelectorAll('[id]')) {
      const id = clean(el.getAttribute('id'));
      if (id) ids.add(id);
    }
  }
  return ids;
}

const clientInfoRequiredEmptyCheck = {
  id: 'client-info-required-empty',
  label: 'Required Client Info empty',
  tier: 'cheap',
  kind: 'content',
  run(state) {
    const findings = [];
    if (!state) return findings;
    validateClientInfoForExport(state);
    const client = state.clientInfo || {};
    if (!clean(client.name)) {
      findings.push(
        makeFinding({
          id: 'name',
          checkId: this.id,
          // Content-tier (Part 0): a missing business name is a content gap, not a
          // structural break — WARNING (bypassable), not a hard block.
          severity: SEVERITY.WARNING,
          accept: { target: 'clientInfo.name', evidence: '' },
          title: 'Business Name is empty',
          detail: 'Set the sitewide business name before publishing.',
          fix: {
            label: 'Set business name',
            kind: 'focus-client-field',
            payload: { path: 'clientInfo.name' }
          }
        })
      );
    }

    const used = collectUsedClientKeys(state);
    const usesContactBinding = used.has('phone') || used.has('phoneFormatted') || used.has('email');
    const normalized = normalizeClientInfo(client, state.projectName);
    if (usesContactBinding && !normalized.phone && !normalized.email) {
      findings.push(
        makeFinding({
          id: 'contact',
          checkId: this.id,
          // Content-tier (Part 0): an empty contact field is a content gap — WARNING.
          severity: SEVERITY.WARNING,
          accept: { target: 'clientInfo.contact', evidence: '' },
          title: 'Phone or Email is required',
          detail: 'This project contains phone or email bindings, but neither field has a value.',
          fix: {
            label: 'Add contact details',
            kind: 'focus-client-field',
            payload: { path: 'clientInfo.email' }
          }
        })
      );
    }
    return findings;
  }
};

const perPageSeoTitleMissingCheck = {
  id: 'per-page-seo-title-missing',
  label: 'SEO title missing',
  tier: 'cheap',
  kind: 'content',
  run(state) {
    return (state?.pages || [])
      .filter((page) => !clean(page?.seo?.title))
      .map((page) =>
        makeFinding({
          id: safeId(page.id || page.name),
          checkId: this.id,
          severity: SEVERITY.WARNING,
          accept: { target: `page:${page.id || page.name}`, evidence: '' },
          title: `SEO title missing on ${page.name || 'page'}`,
          detail: 'Add a page title for search previews and browser tabs.',
          location: { pageId: page.id || null, field: 'seo.title' },
          // v1.2 — cluster together. Per-page Fix → focus-seo-field is the right
          // per-row action (each page needs a unique title); no clusterFix.
          groupKey: 'seo-title-missing',
          groupLabel: 'Pages missing SEO title',
          fix: {
            label: 'Write page title',
            kind: 'focus-seo-field',
            payload: { pageId: page.id, fieldKey: 'title' }
          }
        })
      );
  }
};

const perPageSeoDescriptionMissingCheck = {
  id: 'per-page-seo-description-missing',
  label: 'SEO description missing',
  tier: 'cheap',
  kind: 'content',
  run(state) {
    return (state?.pages || [])
      .filter((page) => !clean(page?.seo?.description))
      .map((page) =>
        makeFinding({
          id: safeId(page.id || page.name),
          checkId: this.id,
          severity: SEVERITY.WARNING,
          accept: { target: `page:${page.id || page.name}`, evidence: '' },
          title: `SEO description missing on ${page.name || 'page'}`,
          detail: 'Add a meta description for search and social previews.',
          location: { pageId: page.id || null, field: 'seo.description' },
          // v1.2 — cluster together. Separate cluster from titles since the fix
          // field differs; combining would conflate two different actions.
          groupKey: 'seo-description-missing',
          groupLabel: 'Pages missing SEO description',
          fix: {
            label: 'Write description',
            kind: 'focus-seo-field',
            payload: { pageId: page.id, fieldKey: 'description' }
          }
        })
      );
  }
};

const eidUniquenessCheck = {
  id: 'eid-uniqueness',
  label: 'Duplicate data-pb-eid',
  tier: 'cheap',
  kind: 'maintenance',
  run(state) {
    const findings = [];
    iterateAllSections(state, (ctx) => {
      // Editable IDs are allocated and resolved within one section.
      const seen = new Map();
      const re = /data-pb-eid=(['"])(.*?)\1/g;
      let match;
      while ((match = re.exec(ctx.html || '')) !== null) {
        const eid = match[2];
        if (!eid) continue;
        const occurrence = (seen.get(eid) || 0) + 1;
        seen.set(eid, occurrence);
        if (occurrence === 1) continue;
        findings.push(
          makeFinding({
            id: `${safeId(eid)}-${safeId(ctx.sectionPath)}-${occurrence}`,
            checkId: this.id,
            severity: SEVERITY.WARNING,
            title: `Duplicate editable ID "${eid}"`,
            detail:
              'Two elements in this section share an editable ID, so edits can land on the wrong one.',
            location: locationFor(ctx, null, { eid }),
            // The dispatcher validates occurrence, comments and collaboration
            // together before committing one reversible repair.
            groupKey: 'eid-uniqueness',
            groupLabel: 'Duplicate editable IDs',
            clusterFix: { kind: 'restamp-all-eids' },
            fix: {
              kind: 'navigate-to-hit',
              label: 'Show duplicate',
              payload: { sectionPath: ctx.sectionPath, instanceId: ctx.instanceId, eid }
            }
          })
        );
      }
    });
    return findings;
  }
};

const boundButEmptyTextCheck = {
  id: 'bound-but-empty-text',
  label: 'Bound Client Info empty',
  tier: 'cheap',
  kind: 'content',
  run(state) {
    return findBoundButEmptyFields(state)
      .filter((entry) => entry.kind === 'text')
      .map((entry, index) =>
        makeFinding({
          id: `${safeId(entry.key)}-${index}-${safeId(entry.sectionPath)}-${safeId(entry.eid)}`,
          checkId: this.id,
          severity: SEVERITY.WARNING,
          title: `${entry.label} binding is empty`,
          detail:
            'This bound text will render as an empty string until the Client Info field is filled.',
          location: {
            pageId: entry.pageId,
            pageName: entry.pageName,
            instanceId: entry.instanceId,
            sectionPath: entry.sectionPath,
            sharedKind: entry.sharedKind,
            eid: entry.eid || null,
            field: entry.path
          },
          fix: { label: 'Fill this in', kind: 'focus-client-field', payload: { path: entry.path } }
        })
      );
  }
};

const templateHotlinkAssetsCheck = {
  id: 'template-hotlink-assets',
  label: 'Template-domain assets',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const sourceDomain = clean(
      state?.sourceTemplateDomain ||
        state?.sourceClientProfile?.sourceDomain ||
        state?.sourceClientProfile?.domain
    );
    if (!sourceDomain) return [];
    const findings = [];
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      const candidates = [];
      for (const el of root.querySelectorAll('[src]')) {
        candidates.push({ el, attr: 'src', value: el.getAttribute('src') || '' });
      }
      for (const el of root.querySelectorAll('[srcset]')) {
        for (const part of (el.getAttribute('srcset') || '').split(',')) {
          candidates.push({ el, attr: 'srcset', value: part.trim().split(/\s+/)[0] || '' });
        }
      }
      candidates.forEach(({ el, attr, value }, index) => {
        if (!isSourceDomainUrl(value, sourceDomain)) return;
        const eid = closestEid(el);
        // A url the liveness sweep proved unrecoverable — dead origin AND not
        // vendored by this template's bundle — is NOT the ordinary pre-export condition below.
        // The exporter cannot download what no longer exists, so it fails at
        // preflight with `asset-download-failed` after a long wait. Saying so
        // here, before the export, is the whole point of this escalation.
        // Host alone is deliberately NOT enough: 1,576 images recovered from the
        // Wayback Machine resolve from the vendored store with their origin
        // still dead, and blocking on those would be a false refusal.
        const unrecoverable =
          typeof ctx.isKnownDeadAssetUrl === 'function' &&
          ctx.isKnownDeadAssetUrl(value, sourceDomain);
        if (unrecoverable) {
          findings.push(
            makeFinding({
              id: `${safeId(sectionCtx.sectionPath)}-${safeId(eid)}-${attr}-${index}-dead`,
              checkId: this.id,
              severity: SEVERITY.BLOCKING,
              accept: {
                target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${eid}`,
                evidence: value
              },
              title: 'Image no longer exists at its source',
              detail:
                `${sourceDomain} no longer serves this image and no copy was captured, so the ` +
                `export cannot download it. Replace or upload a new image.`,
              location: locationFor(sectionCtx, el, { match: value }),
              groupKey: `template-hotlink-dead:${sourceDomain}`,
              groupLabel: `Images permanently missing from ${sourceDomain}`,
              fix: {
                kind: 'open-image-replacer',
                label: 'Choose image',
                payload: {
                  sectionPath: sectionCtx.sectionPath,
                  instanceId: sectionCtx.instanceId,
                  eid
                }
              }
            })
          );
          return;
        }
        findings.push(
          makeFinding({
            id: `${safeId(sectionCtx.sectionPath)}-${safeId(eid)}-${attr}-${index}`,
            checkId: this.id,
            // WARNING, not BLOCKING (2026-08-24). This check reads STATE, before
            // asset resolution — and both exporters resolve with
            // `downloadExternal: true`, so a template-domain URL here is the
            // NORMAL pre-export condition for any template-derived project: the
            // exporter downloads these and bundles them under assets/images/, and
            // the shipped site does not hotlink. Blocking on it refused the export
            // for the very thing the export was about to fix (714 rows on one
            // project). The condition that genuinely ships a hotlink is a download
            // that FAILED, and that is `asset-download-failed` in preflight.js —
            // raised after resolution, still BLOCKING, and unaffected by this.
            // P4: CLEANUP, not WARNING. With P2's adoption this is an
            // invitation to bring the image into the project, not a defect —
            // and the exporter downloads and bundles it either way. Keeping it
            // at WARNING meant a project where nothing is wrong still had a
            // wall of findings.
            severity: SEVERITY.CLEANUP,
            accept: {
              target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${eid}`,
              evidence: value
            },
            title: 'Image still loads from the template domain',
            detail: `This will be downloaded and bundled into the export. Replace or upload it to stop depending on ${sourceDomain} in the builder.`,
            location: locationFor(sectionCtx, el, { match: value }),
            // groupKey/groupLabel — every hotlink finding on a project shares the
            // same source domain, so they roll into one cluster in the audit tab.
            // (v1.1 — see pre-publish-audit-tab.js _renderClusteredFindings.)
            groupKey: `template-hotlink:${sourceDomain}`,
            groupLabel: `Images loading from ${sourceDomain}`,
            // P2: one verb for the whole cluster. Downloads every state-owned
            // remote image into the project's AssetStore and rewrites the
            // references, in ONE history entry.
            clusterFix: { kind: 'adopt-assets' },
            // fix.kind upgraded to 'open-image-replacer' in v1.1 — actions module
            // now navigates AND opens the image picker via image-editor.js's
            // editing:request-image-replace subscriber.
            fix: {
              kind: 'open-image-replacer',
              label: 'Choose image',
              payload: {
                sectionPath: sectionCtx.sectionPath,
                instanceId: sectionCtx.instanceId,
                eid
              }
            }
          })
        );
      });
    });
    return findings;
  }
};

const logoSentinelLeakedCheck = {
  id: 'logo-sentinel-leaked',
  label: 'Logo placeholder leaked',
  tier: 'full',
  kind: 'content',
  run(state) {
    const findings = [];
    iterateAllSections(state, (ctx) => {
      const index = (ctx.html || '').indexOf(LOGO_SENTINEL_VALUE);
      if (index === -1) return;
      const eid = closestEidFromHTML(ctx.html, index);
      findings.push(
        makeFinding({
          id: `${safeId(ctx.sectionPath)}-${index}`,
          checkId: this.id,
          severity: SEVERITY.BLOCKING,
          title: 'Logo placeholder is still present',
          detail: 'Replace the generated logo placeholder before publishing.',
          location: locationFor(ctx, null, {
            eid,
            ...snippet(ctx.html, index, LOGO_SENTINEL_VALUE.length)
          }),
          fix: {
            label: 'Replace logo',
            kind: 'navigate-to-hit',
            payload: { sectionPath: ctx.sectionPath, instanceId: ctx.instanceId, eid }
          }
        })
      );
    });
    return findings;
  }
};

function closestEidFromHTML(html, index) {
  const matches = [
    ...String(html || '')
      .slice(0, index)
      .matchAll(/data-pb-eid=(['"])(.*?)\1/g)
  ];
  return matches.length ? matches[matches.length - 1][2] : null;
}

function isDecorativeImage(img) {
  if (!img) return false;
  // alt="" is the HTML-native decorative-image contract. It is intentionally
  // distinct from a missing alt attribute.
  if (img.hasAttribute('alt') && img.getAttribute('alt') === '') return true;
  if ((img.getAttribute('role') || '').toLowerCase() === 'presentation') return true;
  if ((img.getAttribute('aria-hidden') || '').toLowerCase() === 'true') return true;
  return !!img.closest?.(
    '[role="presentation"],[aria-hidden="true"],.cs-decorative-svg,.pb-decorative-svg'
  );
}

const missingImageAltCheck = {
  id: 'missing-image-alt',
  label: 'Image alt missing',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      Array.from(root.querySelectorAll('img')).forEach((img, index) => {
        if (isDecorativeImage(img)) return;
        if (img.hasAttribute('alt') && clean(img.getAttribute('alt'))) return;
        const eid = closestEid(img);
        findings.push(
          makeFinding({
            id: `${safeId(sectionCtx.sectionPath)}-${safeId(eid)}-${index}`,
            checkId: this.id,
            severity: SEVERITY.WARNING,
            accept:
              sectionCtx.instanceId && eid
                ? {
                    target: `${sectionCtx.instanceId}/${eid}`,
                    evidence: img.getAttribute('src') || ''
                  }
                : null,
            groupKey: 'missing-image-alt',
            groupLabel: 'Images with no alt text',
            clusterFix: { kind: 'mark-decorative', label: 'Mark decorative' },
            title: 'Image is missing alt text',
            detail:
              'Add descriptive alt text, or mark the image decorative if it should be ignored by assistive tech.',
            location: locationFor(sectionCtx, img),
            fix: {
              label: 'Add alt text',
              kind: 'navigate-to-hit',
              payload: {
                sectionPath: sectionCtx.sectionPath,
                instanceId: sectionCtx.instanceId,
                eid,
                imageIndex: index,
                src: img.getAttribute('src') || ''
              }
            }
          })
        );
      });
    });
    return findings;
  }
};

const CTA_CLASS_RE = /\b(cs-button-solid|cs-button-stroke|pb-button|cs-link)\b/;

const emptyCtaHrefCheck = {
  id: 'empty-cta-href',
  label: 'CTA link missing URL',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      Array.from(root.querySelectorAll('a')).forEach((anchor, index) => {
        const href = anchor.getAttribute('href');
        const className = anchor.getAttribute('class') || '';
        const looksLikeCta =
          CTA_CLASS_RE.test(className) ||
          (anchor.getAttribute('role') || '').toLowerCase() === 'button';
        if (!looksLikeCta) return;
        if (href && href.trim() && href.trim() !== '#') return;
        const eid = closestEid(anchor);
        findings.push(
          makeFinding({
            id: `${safeId(sectionCtx.sectionPath)}-${index}`,
            checkId: this.id,
            severity: SEVERITY.WARNING,
            groupKey: 'empty-cta-href',
            groupLabel: 'Buttons with no destination',
            accept:
              sectionCtx.instanceId && eid
                ? {
                    target: `${sectionCtx.instanceId}/${eid}`,
                    evidence: (anchor.textContent || '').trim()
                  }
                : null,
            title: 'CTA link has no destination',
            detail: 'Set the button or link destination before publishing.',
            location: locationFor(sectionCtx, anchor, { match: clean(anchor.textContent) }),
            fix: {
              kind: 'open-link-editor',
              label: 'Set link',
              payload: {
                sectionPath: sectionCtx.sectionPath,
                instanceId: sectionCtx.instanceId,
                eid
              }
            }
          })
        );
      });
    });
    return findings;
  }
};

const internalLinkResolutionCheck = {
  id: 'internal-link-broken',
  label: 'Internal link broken',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const validPaths = pageSlugPaths(state, ctx.generateSlug);
    return scanAllLinks(state, { parseHTML: ctx.parseHTML }).flatMap((link, index) => {
      const href = normalizeInternalHref(link.anchorSignature.href);
      if (href == null) return [];
      const normalized = href.replace(/\/+$/g, '') || '/';
      if (validPaths.has(normalized) || validPaths.has(normalized.replace(/^\//, ''))) return [];
      const eid = link.anchorSignature.eid || null;
      return makeFinding({
        id: `${index}-${safeId(normalized)}-${safeId(link.statePath)}`,
        checkId: this.id,
        severity: SEVERITY.WARNING,
        groupKey: 'internal-link-broken',
        groupLabel: 'Links pointing at pages that do not exist',
        accept: {
          target: `${link.sectionInstanceId || link.statePath}/${link.anchorSignature.eid || ''}`,
          evidence: link.anchorSignature.href || ''
        },
        title: `Internal link does not match a page: ${link.anchorSignature.href || '(empty)'}`,
        detail: 'Point this link to an existing page or an external URL.',
        location: {
          pageId: link.pageId,
          pageName: link.pageName,
          instanceId: link.sectionInstanceId,
          sectionPath: link.statePath,
          sharedKind: link.sharedKind,
          eid,
          match: link.anchorSignature.href
        },
        fix: {
          label: 'Set link',
          kind: 'open-link-editor',
          payload: { sectionPath: link.statePath, instanceId: link.sectionInstanceId, eid }
        }
      });
    });
  }
};

const danglingAnchorCheck = {
  id: 'dangling-anchor',
  label: 'Dangling anchor link',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const contexts = allPageContexts(state);
    const idsByPage = new Map(
      contexts.map((pageCtx) => [pageCtx.page.id, collectIdsForPage(pageCtx, ctx.parseHTML)])
    );
    const nameByPage = new Map(
      contexts.map((pageCtx) => [pageCtx.page.id, pageCtx.page.name || pageCtx.page.id || ''])
    );

    return scanAllLinks(state, { parseHTML: ctx.parseHTML }).flatMap((link, index) => {
      const href = clean(link.anchorSignature.href);
      if (!href.startsWith('#') || href === '#') return [];
      const target = decodeURIComponent(href.slice(1).split('?')[0]);
      const eid = link.anchorSignature.eid || null;

      // A SHARED-section link renders on every page, so it must be judged on
      // every page. The pre-P4 code tested it against the UNION of all page
      // ids, which meant an anchor defined on ONE page silently passed
      // everywhere — a false negative that shipped broken anchors. The union is
      // gone; what replaces it is a per-page evaluation collapsed into ONE
      // finding whose evidence names the pages where the target is missing.
      const brokenOn = link.pageId
        ? (idsByPage.get(link.pageId) || new Set()).has(target)
          ? []
          : [link.pageId]
        : [...idsByPage.entries()].filter(([, ids]) => !ids.has(target)).map(([id]) => id);
      if (brokenOn.length === 0) return [];

      const pageNames = brokenOn.map((id) => nameByPage.get(id) || id);
      const shared = !link.pageId;
      const where = shared
        ? ` — missing on ${pageNames.length === contexts.length ? 'every page' : pageNames.join(', ')}`
        : '';
      return makeFinding({
        id: `${index}-${safeId(target)}-${safeId(link.statePath)}`,
        checkId: this.id,
        severity: SEVERITY.WARNING,
        title: `Anchor target not found: #${target}${where}`,
        detail: shared
          ? 'This link is in a shared section, so it renders on every page. The target ID is missing on the pages listed above.'
          : 'This hash link points to an ID that does not exist on the page.',
        location: {
          pageId: link.pageId,
          pageName: link.pageName,
          instanceId: link.sectionInstanceId,
          sectionPath: link.statePath,
          sharedKind: link.sharedKind,
          eid,
          match: href,
          // The exact affected-page list is evidence, not decoration: it is what
          // the accept fingerprint is built from, so adding a page that breaks
          // the anchor invalidates a prior accept instead of silently inheriting it.
          brokenOnPageIds: brokenOn
        },
        groupKey: 'dangling-anchor',
        groupLabel: 'Links pointing at missing anchors',
        accept: {
          target: `${link.sectionInstanceId || link.statePath}/${eid || target}`,
          evidence: `${href}|${[...brokenOn].sort().join(',')}`
        },
        fix: {
          kind: 'open-link-editor',
          label: 'Set link',
          payload: { sectionPath: link.statePath, instanceId: link.sectionInstanceId, eid }
        }
      });
    });
  }
};

const PLACEHOLDER_PATTERNS = [
  /lorem ipsum/i,
  /your business name here/i,
  /add your text/i,
  /click to edit/i,
  /your\.(?:email|phone)/i,
  /123-456-7890/,
  /\bTODO\b/,
  /\bFIXME\b/,
  /\bPLACEHOLDER\b/
];

const placeholderTextCheck = {
  id: 'placeholder-text',
  label: 'Placeholder text',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      walkTextNodes(
        root,
        (node) => {
          const text = node.nodeValue || '';
          const patterns = [...PLACEHOLDER_PATTERNS];
          if (node.parentElement?.closest?.('a')) patterns.push(/example\.com/i);
          for (const pattern of patterns) {
            pattern.lastIndex = 0;
            const match = pattern.exec(text);
            if (!match) continue;
            const loc = textLocation(sectionCtx, node, match.index, match[0]);
            findings.push(
              makeFinding({
                id: `${safeId(sectionCtx.sectionPath)}-${safeId(loc.eid)}-${safeId(match[0])}-${match.index}`,
                checkId: this.id,
                severity: SEVERITY.CLEANUP,
                groupKey: 'placeholder-text',
                groupLabel: 'Template placeholder copy still in place',
                accept: {
                  target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${loc.eid}`,
                  evidence: match[0]
                },
                title: `Placeholder text left behind: "${match[0]}"`,
                detail: 'Replace template placeholder copy with final content.',
                location: loc,
                fix: {
                  label: 'Replace text',
                  kind: 'navigate-to-hit',
                  payload: {
                    sectionPath: sectionCtx.sectionPath,
                    instanceId: sectionCtx.instanceId,
                    eid: loc.eid
                  }
                }
              })
            );
            break;
          }
        },
        { includeFormText: true, includeBoundText: true }
      );
    });
    return findings;
  }
};

function sourceNeedles(profile) {
  const values = [
    profile?.businessName,
    ...(Array.isArray(profile?.businessNameVariants) ? profile.businessNameVariants : []),
    ...(Array.isArray(profile?.phoneVariants) ? profile.phoneVariants : []),
    profile?.email
  ];
  return [...new Set(values.map(clean).filter((value) => value.length >= 4))];
}

const sourceProfileLeftoverCheck = {
  id: 'source-profile-leftover',
  label: 'Source profile leftover',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const needles = sourceNeedles(state?.sourceClientProfile);
    if (!needles.length) return [];
    const findings = [];
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      walkTextNodes(
        root,
        (node) => {
          const text = node.nodeValue || '';
          for (const needle of needles) {
            const index = text.toLowerCase().indexOf(needle.toLowerCase());
            if (index === -1) continue;
            const loc = textLocation(
              sectionCtx,
              node,
              index,
              text.slice(index, index + needle.length)
            );
            findings.push(
              makeFinding({
                id: `${safeId(sectionCtx.sectionPath)}-${safeId(loc.eid)}-${safeId(needle)}-${index}`,
                checkId: this.id,
                severity: SEVERITY.CLEANUP,
                accept: {
                  target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${loc.eid}`,
                  evidence: needle
                },
                title: `Source business text remains: "${text.slice(index, index + needle.length)}"`,
                detail: 'Run Resync from Client Info or replace this leftover source-profile copy.',
                location: loc,
                fix: { label: 'Resync from Client Info', kind: 'open-resync-prompt', payload: {} }
              })
            );
            break;
          }
        },
        { includeFormText: true, includeBoundText: true }
      );
    });
    return findings;
  }
};

const skinReviewMarkersCheck = {
  id: 'skin-review-markers',
  label: 'Skin from URL review markers',
  tier: 'cheap',
  kind: 'content',
  run(state, ctx) {
    // Skin from URL stamps unresolved work as data-pb-needs-upload /
    // data-pb-needs-review / data-pb-source-asset. Export strips all
    // data-pb-* attributes, which would erase these markers silently —
    // surface them here BEFORE they disappear, using the importReport
    // bucket vocabulary.
    const manifestRows = Array.isArray(state?.copyReview?.rows) ? state.copyReview.rows : [];
    const manifestKinds = new Set(manifestRows.map((row) => clean(row?.kind)).filter(Boolean));
    const counts = new Map(); // key `${marker}:${value}` -> count
    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      for (const marker of [
        'data-pb-needs-upload',
        'data-pb-needs-review',
        'data-pb-source-asset'
      ]) {
        for (const el of root.querySelectorAll(`[${marker}]`)) {
          const value = clean(el.getAttribute(marker)) || 'unspecified';
          // Copy-audit markers have a richer persisted manifest row below. Do
          // not render a locationless aggregate for the exact same evidence.
          if (marker === 'data-pb-needs-review' && manifestKinds.has(value)) continue;
          const key = `${marker}:${value}`;
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      }
    });

    const findings = [];
    for (const [key, count] of counts) {
      const [marker, value] = key.split(':');
      const isUpload = marker === 'data-pb-needs-upload';
      const isSourceAsset = marker === 'data-pb-source-asset';
      findings.push(
        makeFinding({
          id: `${safeId(marker)}-${safeId(value)}`,
          checkId: this.id,
          severity: isSourceAsset ? SEVERITY.CLEANUP : SEVERITY.WARNING,
          title: isUpload
            ? `${count} slot(s) still need an upload (${value})`
            : isSourceAsset
              ? `${count} preserved source asset(s) (${value})`
              : `${count} item(s) flagged for review (${value})`,
          detail: isUpload
            ? 'Skin from URL left a placeholder here — upload a replacement image before publishing.'
            : isSourceAsset
              ? 'A source-template asset remains for visual fidelity; review or rehost it before publishing.'
              : 'Skin from URL could not resolve this item to client data — review it before publishing.',
          groupKey: this.id,
          groupLabel: 'Skin from URL leftovers'
        })
      );
    }

    for (const [index, row] of manifestRows.entries()) {
      const pageIndex = (state?.pages || []).findIndex(
        (page) => clean(page?.name) === clean(row?.pageName)
      );
      const page = pageIndex >= 0 ? state.pages[pageIndex] : null;
      const sectionIndex = (page?.sections || []).findIndex(
        (section) =>
          clean(section?.sectionId) === clean(row?.sectionId) ||
          clean(section?.catalogKey) === clean(row?.sectionId) ||
          clean(section?.instanceId) === clean(row?.sectionId)
      );
      const section = sectionIndex >= 0 ? page.sections[sectionIndex] : null;
      findings.push(
        makeFinding({
          id: `copy-${index}-${safeId(row?.kind)}-${safeId(row?.pageName)}-${safeId(row?.sectionId)}`,
          checkId: this.id,
          severity: SEVERITY.WARNING,
          title: `Copy needs review (${clean(row?.kind) || 'copy'})`,
          detail: clean(row?.detail) || 'Generated copy needs review before publishing.',
          location: {
            pageId: page?.id || null,
            pageName: clean(row?.pageName) || clean(page?.name),
            instanceId: section?.instanceId || null,
            sectionPath:
              pageIndex >= 0 && sectionIndex >= 0
                ? `pages.${pageIndex}.sections.${sectionIndex}.html`
                : clean(row?.sectionId),
            sharedKind: null,
            eid: null
          },
          groupKey: this.id,
          groupLabel: 'Skin from URL leftovers'
        })
      );
    }
    if (Number(state?.copyReview?.truncated) > 0) {
      findings.push(
        makeFinding({
          id: 'copy-review-overflow',
          checkId: this.id,
          severity: SEVERITY.WARNING,
          title: `${state.copyReview.truncated} additional copy finding(s) need review`,
          detail:
            'The persisted copy-review manifest reached its bound. Review the affected project copy before publishing.',
          groupKey: this.id,
          groupLabel: 'Skin from URL leftovers'
        })
      );
    }
    return findings;
  }
};

function headingLevel(el) {
  const tag = el?.tagName || '';
  return /^H[1-6]$/.test(tag) ? Number(tag.slice(1)) : 0;
}

const headingStructureCheck = {
  id: 'heading-skip',
  label: 'Heading level skipped',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    for (const pageCtx of allPageContexts(state)) {
      let previous = 0;
      for (const sectionCtx of pageCtx.sections) {
        const root = parseSection(sectionCtx, ctx.parseHTML);
        if (!root) continue;
        for (const heading of root.querySelectorAll('h1,h2,h3,h4,h5,h6')) {
          const level = headingLevel(heading);
          if (previous && level - previous > 1) {
            const eid = closestEid(heading);
            findings.push(
              makeFinding({
                id: `${safeId(pageCtx.page.id)}-${safeId(sectionCtx.sectionPath)}-${safeId(eid)}-${level}`,
                checkId: this.id,
                severity: SEVERITY.CLEANUP,
                accept: {
                  target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${eid}`,
                  evidence: `H${previous}->H${level}`
                },
                title: `Heading jumps from H${previous} to H${level}`,
                detail: 'Keep heading levels sequential so the page outline is easier to scan.',
                location: locationFor(sectionCtx, heading, {
                  pageId: pageCtx.page.id,
                  pageName: pageCtx.page.name || '',
                  match: clean(heading.textContent)
                }),
                fix: {
                  label: 'Fix heading level',
                  kind: 'navigate-to-hit',
                  payload: {
                    sectionPath: sectionCtx.sectionPath,
                    instanceId: sectionCtx.instanceId,
                    eid
                  }
                }
              })
            );
          }
          previous = level;
        }
      }
    }
    return findings;
  }
};

const multipleH1Check = {
  id: 'multiple-h1',
  label: 'Multiple H1s',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    for (const pageCtx of allPageContexts(state)) {
      let h1Count = 0;
      for (const sectionCtx of pageCtx.sections) {
        const root = parseSection(sectionCtx, ctx.parseHTML);
        if (!root) continue;
        for (const heading of root.querySelectorAll('h1')) {
          h1Count++;
          if (h1Count <= 1) continue;
          const eid = closestEid(heading);
          findings.push(
            makeFinding({
              id: `${safeId(pageCtx.page.id)}-${safeId(sectionCtx.sectionPath)}-${safeId(eid)}-${h1Count}`,
              checkId: this.id,
              severity: SEVERITY.CLEANUP,
              accept:
                sectionCtx.instanceId && eid
                  ? {
                      target: `${sectionCtx.instanceId}/${eid}`,
                      evidence: heading.textContent || ''
                    }
                  : null,
              title: `Additional H1 on ${pageCtx.page.name || 'page'}`,
              detail:
                'Most pages should have one primary H1. Consider changing this heading to H2 or lower.',
              location: locationFor(sectionCtx, heading, {
                pageId: pageCtx.page.id,
                pageName: pageCtx.page.name || '',
                match: clean(heading.textContent)
              }),
              fix: {
                label: 'Change heading',
                kind: 'navigate-to-hit',
                payload: {
                  sectionPath: sectionCtx.sectionPath,
                  instanceId: sectionCtx.instanceId,
                  eid
                }
              }
            })
          );
        }
      }
    }
    return findings;
  }
};

const missingH1Check = {
  id: 'missing-h1',
  label: 'Page missing H1',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    for (const pageCtx of allPageContexts(state)) {
      let parsedSections = 0;
      let h1Count = 0;
      for (const sectionCtx of pageCtx.sections) {
        const root = parseSection(sectionCtx, ctx.parseHTML);
        if (!root) continue;
        parsedSections++;
        h1Count += root.querySelectorAll('h1').length;
      }
      if (parsedSections === 0 || h1Count > 0) continue;
      findings.push(
        makeFinding({
          id: safeId(pageCtx.page.id || pageCtx.page.name),
          checkId: this.id,
          severity: SEVERITY.WARNING,
          accept: { target: `page:${pageCtx.page.id || pageCtx.page.name}`, evidence: '' },
          title: `No H1 heading on ${pageCtx.page.name || 'page'}`,
          detail:
            'Every page should have one primary H1 — search engines and screen readers treat it as the page’s main topic.',
          location: { pageId: pageCtx.page.id || null, pageName: pageCtx.page.name || '' },
          groupKey: 'missing-h1',
          groupLabel: 'Pages without an H1 heading'
        })
      );
    }
    return findings;
  }
};

// Text/background pairs this check can honestly judge from state alone. The
// theme half mirrors the Design panel's pairing (text tokens over the site's
// light surface; the white token over the header color). Anything needing
// live computed styles belongs to the in-canvas badge, not this check.
const THEME_CONTRAST_PAIRS = [
  { fg: '--headerColor', bg: null, label: 'Headings' },
  { fg: '--bodyTextColor', bg: null, label: 'Body text' },
  { fg: '--primary', bg: null, label: 'Accent text' },
  { fg: '--bodyTextColorWhite', bg: '--headerColor', label: 'Light text' }
];

const colorContrastCheck = {
  id: 'color-contrast',
  label: 'Color contrast (heuristic)',
  tier: 'full',
  kind: 'content',
  run(state, ctx) {
    const findings = [];
    const theme = state?.theme && typeof state.theme === 'object' ? state.theme : {};
    const surface = parseCssColor(theme['--bodyTextColorWhite'] || '#ffffff', {
      variables: theme
    });
    for (const pair of THEME_CONTRAST_PAIRS) {
      const fgValue = theme[pair.fg];
      if (!fgValue) continue;
      const fg = parseCssColor(fgValue, { variables: theme });
      const bg = pair.bg ? parseCssColor(theme[pair.bg] || '', { variables: theme }) : surface;
      if (!fg || !bg || (bg.a != null && bg.a < 0.999)) continue;
      const assessment = assessContrast(fg, bg);
      if (!assessment || assessment.aa) continue;
      findings.push(
        makeFinding({
          id: safeId(pair.fg),
          checkId: this.id,
          severity: SEVERITY.WARNING,
          accept: { target: `theme:${pair.fg}`, evidence: String(fgValue) },
          title: `${pair.label} theme color may fail WCAG contrast (${assessment.label})`,
          detail:
            'Heuristic check of declared theme colors — actual rendering can differ where CSS overrides them. WCAG AA needs 4.5:1 for normal text (3:1 for 24px+ text).',
          location: { field: `theme.${pair.fg}` },
          groupKey: 'theme-contrast',
          groupLabel: 'Theme colors with low contrast'
        })
      );
    }

    iterateAllSections(state, (sectionCtx) => {
      const root = parseSection(sectionCtx, ctx.parseHTML);
      if (!root) return;
      for (const el of root.querySelectorAll('[style]')) {
        const fgValue = clean(el.style?.getPropertyValue?.('color'));
        const bgValue = clean(el.style?.getPropertyValue?.('background-color'));
        if (!fgValue || !bgValue) continue;
        const fg = parseCssColor(fgValue, { variables: theme });
        const bg = parseCssColor(bgValue, { variables: theme });
        if (!fg || !bg || (bg.a != null && bg.a < 0.999)) continue;
        const assessment = assessContrast(fg, bg);
        if (!assessment || assessment.aa) continue;
        const eid = closestEid(el);
        findings.push(
          makeFinding({
            id: `${safeId(sectionCtx.sectionPath)}-${safeId(eid)}`,
            checkId: this.id,
            severity: SEVERITY.WARNING,
            accept: {
              target: `${sectionCtx.instanceId || sectionCtx.sectionPath}/${eid}`,
              evidence: `${fgValue} on ${bgValue}`
            },
            title: `Declared text/background colors may fail WCAG contrast (${assessment.label})`,
            detail:
              'Heuristic check of inline declared colors — CSS may override what actually renders. WCAG AA needs 4.5:1 for normal text (3:1 for 24px+ text).',
            location: locationFor(sectionCtx, el, { match: `${fgValue} on ${bgValue}` }),
            groupKey: 'inline-contrast',
            groupLabel: 'Elements with low declared contrast',
            fix: {
              label: 'Adjust colours',
              kind: 'navigate-to-hit',
              payload: {
                sectionPath: sectionCtx.sectionPath,
                instanceId: sectionCtx.instanceId,
                eid
              }
            }
          })
        );
      }
    });
    return findings;
  }
};

const missingOgImageCheck = {
  id: 'missing-og-image',
  label: 'Social share image missing',
  tier: 'cheap',
  kind: 'content',
  run(state) {
    return (state?.pages || [])
      .filter((page) => !clean(page?.seo?.ogImage))
      .map((page) =>
        makeFinding({
          id: safeId(page.id || page.name),
          checkId: this.id,
          severity: SEVERITY.WARNING,
          accept: { target: `page:${page.id || page.name}`, evidence: '' },
          title: `Social share image missing on ${page.name || 'page'}`,
          detail:
            'Add an Open Graph image so links shared on social media show a preview card instead of a blank tile.',
          location: { pageId: page.id || null, field: 'seo.ogImage' },
          groupKey: 'og-image-missing',
          groupLabel: 'Pages missing a social share image',
          fix: {
            label: 'Choose share image',
            kind: 'focus-seo-field',
            payload: { pageId: page.id, fieldKey: 'ogImage' }
          }
        })
      );
  }
};

export const CHECKS = [
  clientInfoRequiredEmptyCheck,
  perPageSeoTitleMissingCheck,
  perPageSeoDescriptionMissingCheck,
  eidUniquenessCheck,
  boundButEmptyTextCheck,
  templateHotlinkAssetsCheck,
  logoSentinelLeakedCheck,
  missingImageAltCheck,
  emptyCtaHrefCheck,
  internalLinkResolutionCheck,
  danglingAnchorCheck,
  placeholderTextCheck,
  sourceProfileLeftoverCheck,
  skinReviewMarkersCheck,
  headingStructureCheck,
  multipleH1Check,
  missingH1Check,
  missingOgImageCheck,
  colorContrastCheck
];

/** Backfill kind for persisted and external findings at the disposition boundary. */
export const CHECK_KIND = Object.freeze(
  Object.fromEntries(CHECKS.map((check) => [check.id, check.kind]))
);
